import { endpoint } from '@shared/services/endpoint'
import { getRequest, sendRequest } from '@shared/services/fetcher'
import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'

import type { Order } from '../../order/libs/type'
import type { BankAccount, CheckoutFormValues } from './type'

type CreateCheckoutResponse = {
  key: string
  orderId: string
  redirect: string
  result: string
}

type OrderReceivedRequest = {
  key: string
  orderId: string
}

type OmisePromptpayResponse = {
  expiresAt?: Date
  image?: string
  status: 'expired' | 'failed' | 'pending' | 'successful'
}

export function useCheckout() {
  const useCreateCheckout = () => {
    const {
      data,
      error,
      isMutating: isLoading,
      trigger,
    } = useSWRMutation<
      CreateCheckoutResponse,
      Error,
      string,
      CheckoutFormValues
    >(endpoint.frontend.CHECKOUT.INDEX, sendRequest)

    const checkout = async (request: CheckoutFormValues) => {
      return await trigger(request)
    }

    return { checkout, data, error, isLoading }
  }

  const useOrderReceived = (request: OrderReceivedRequest) => {
    return useSWR<Order, Error, string>(
      endpoint.frontend.CHECKOUT.ORDER_RECEIVED,
      async (url) => {
        return await fetch(url, {
          body: JSON.stringify(request),
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'POST',
        }).then((res) => res.json())
      },
    )
  }

  const useGetBankAccount = () => {
    return useSWR<BankAccount[], Error, string>(
      endpoint.frontend.CHECKOUT.PAYMENT.BANK_ACCOUNTS,
      getRequest,
    )
  }

  const useGetOmisePromptpay = (orderId: number) => {
    return useSWR<OmisePromptpayResponse, Error, string>(
      `${endpoint.frontend.CHECKOUT.PAYMENT.OMISE_PROMPTPAY}/${orderId}`,
      getRequest,
    )
  }

  return {
    createCheckout: useCreateCheckout,
    getBankAccount: useGetBankAccount,
    getOmisePromptpay: useGetOmisePromptpay,
    orderReceived: useOrderReceived,
  }
}
